"use client";

import { useState, useEffect } from "react";

export default function PnlCumulativePage({
  globalFile,
  globalSheet,
  availableSheets: propSheets = [],
}) {
  const [localFile, setLocalFile] = useState(null);
  const [localSheets, setLocalSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [htmlContent, setHtmlContent] = useState("");
  const [auditMeta, setAuditMeta] = useState(null);

  const sheets = propSheets.length > 0 ? propSheets : localSheets;
  const file = globalFile || localFile;
  const activeSheet = selectedSheet || globalSheet || (sheets.length > 0 ? sheets[0] : "");

  const processFile = async (uploaded, sheetOverride = "") => {
    setLocalFile(uploaded);
    setError("");
    setHtmlContent("");

    const formData = new FormData();
    formData.append("file", uploaded);

    try {
      setLoading(true);

      const res = await fetch("/api/inspect", {
        method: "POST",
        body: formData,
      });

      const data = await res.json();

      if (res.ok && data.sheetNames) {
        setLocalSheets(data.sheetNames);
        const targetSheet =
          sheetOverride ||
          globalSheet ||
          data.activeSheetName ||
          data.sheetNames[0];
        setSelectedSheet(targetSheet);
      }
    } catch (err) {
      console.warn("Workbook auto-inspect error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let ignore = false;

    if (globalFile) {
      const formData = new FormData();
      formData.append("file", globalFile);

      fetch("/api/inspect", {
        method: "POST",
        body: formData,
      })
        .then((res) => res.json())
        .then((data) => {
          if (ignore) return;
          if (data?.sheetNames) {
            setLocalSheets(data.sheetNames);
            const targetSheet =
              globalSheet ||
              data.activeSheetName ||
              data.sheetNames[0];
            setSelectedSheet(targetSheet);
          }
        })
        .catch((err) => {
          if (!ignore) {
            console.warn("Workbook auto-inspect error:", err);
          }
        });
    }

    return () => {
      ignore = true;
    };
  }, [globalFile, globalSheet]);

  const handleGenerate = async (e) => {
    e?.preventDefault();

    setLoading(true);
    setError("");
    setHtmlContent("");
    setAuditMeta(null);

    try {
      if (!file) {
        throw new Error("Please select an Excel workbook file.");
      }

      const formData = new FormData();
      formData.append("file", file);

      if (activeSheet) {
        formData.append("sheet", activeSheet);
      }

      const res = await fetch("/api/pnl-cumulative", {
        method: "POST",
        body: formData,
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || "Failed to generate P&L from file.");
      }

      setHtmlContent(data.html);

      setAuditMeta({
        periods: data.report.periodKeys,
        sheet: data.activeSheetName,
        entity: data.detectedEntityId || "From Uploaded File",
        report: data.report,
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 p-6 md:p-10">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
          <div className="border-b border-slate-100 pb-5">
            <h1 className="text-2xl font-bold text-slate-800">
              Profit & Loss (Cumulative Multi-Period)
            </h1>
            <p className="text-sm text-slate-500">
              Generate cumulative P&L statements dynamically across all detected
              periods.
            </p>
          </div>

          <form
            onSubmit={handleGenerate}
            className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4 items-end"
          >
            <div className="sm:col-span-2">
              {!globalFile ? (
                <>
                  <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">
                    Excel Workbook File
                  </label>
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) processFile(f);
                    }}
                    className="w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 cursor-pointer border border-slate-300 rounded-lg"
                  />
                </>
              ) : (
                <div className="text-sm text-slate-600 font-medium">
                  Using Workspace File:{" "}
                  <span className="text-sky-600">{globalFile.name}</span>
                </div>
              )}
            </div>

            {/* Local Sheet Override */}
            {sheets.length > 0 && (
              <div>
                <label className="block text-xs font-semibold text-slate-600 uppercase mb-1">
                  Sheet{" "}
                  {globalSheet && activeSheet === globalSheet
                    ? "(Global Default)"
                    : "(Overridden)"}
                </label>
                <select
                  value={activeSheet}
                  onChange={(e) => setSelectedSheet(e.target.value)}
                  className={`w-full px-3 py-2 bg-white border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 ${
                    activeSheet === globalSheet
                      ? "border-slate-300"
                      : "border-amber-500 text-amber-700"
                  }`}
                >
                  {sheets.map((sh, i) => (
                    <option key={i} value={sh}>
                      {sh}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={loading || !file}
                className="w-full py-2.5 px-4 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white font-semibold text-sm rounded-lg shadow-sm transition-colors"
              >
                {loading ? "Processing..." : "Generate Multi-Period P&L"}
              </button>
            </div>
          </form>

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">
              {error}
            </div>
          )}
        </div>

        {/* Audit Meta Bar */}
        {auditMeta && (
          <div className="flex flex-wrap items-center justify-between gap-4 px-4 py-3 bg-slate-200/60 rounded-lg text-xs text-slate-700">
            <div>
              <span className="font-semibold">Entity:</span> {auditMeta.entity}
              {auditMeta.sheet && (
                <span className="ml-3">
                  <span className="font-semibold">Sheet:</span>{" "}
                  {auditMeta.sheet}
                </span>
              )}
              <span className="ml-3">
                <span className="font-semibold">Evaluated Periods:</span>{" "}
                {auditMeta.periods.join(", ")}
              </span>
            </div>

            <div>
              <button
                type="button"
                onClick={() => window.print()}
                className="px-3 py-1 bg-white hover:bg-slate-100 border border-slate-300 font-semibold rounded shadow-sm text-slate-800"
              >
                Print / Save PDF
              </button>
            </div>
          </div>
        )}

        {/* P&L Statement Render */}
        {htmlContent && (
          <div className="bg-white p-4 rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
          </div>
        )}
      </div>
    </div>
  );
}